/**
 * The four courses of action offered for answers built on figures that are about to change.
 *
 * In `utils/` rather than beside the hook because both the hook and the pure follow-up planner need them, and
 * a shared constant that lives in a component file is how two copies of a string get made.
 *
 * | option | how | applied to the answer | stage |
 * |---|---|---|---|
 * | NOTHING | — | — | unchanged |
 * | DRAFT | AI re-draft | no — draft only | unchanged |
 * | APPLY_AND_REOPEN | find-and-replace of the citations | yes | submitted/approved → In Progress |
 * | APPLY | find-and-replace of the citations | yes | unchanged |
 *
 * The find-and-replace (`POST /foundation-data-v2/answer-figure-update`) rewrites only the inline foundation
 * citations — the figure inside the tag — and none of the surrounding wording. An answer that uses a figure
 * with no inline citation has nothing to rewrite and is reported back for a person to update.
 *
 * APPLY is the one that can alter an approved disclosure and leave it looking approved. That is what was
 * asked for; it is also why it is the option that warrants a second confirmation.
 */
export const IMPACT_OPTION = {
  /** Commit the figure change and leave every answer exactly as it is. */
  NOTHING: "nothing",
  /** Re-draft each affected answer with the new figures, applying nothing and moving no stage. */
  DRAFT: "draft",
  /** Rewrite the citations, and return submitted or approved requirements to In Progress. */
  APPLY_AND_REOPEN: "applyAndReopen",
  /** Rewrite the citations, leaving every stage where it is. */
  APPLY: "apply",
};

/**
 * How far a requirement has got, coarsened to the groups a person can act on — the frontend's view of
 * `FoundationImpactBucket`.
 *
 * Shared because two surfaces show a bucket: the "Manage changed data" cards and a datapoint's usage list. The
 * colour is the part worth sharing — orange has to mean "submitted or approved" in both, or the same
 * requirement reads as two different levels of risk depending on which dialog you opened it from.
 *
 * `SUBMITTED_OR_APPROVED` is not called APPROVED: `INTERNAL_REVIEW` is "Ready for Approval", which is submitted
 * and not signed off.
 */
export const IMPACT_BUCKET = {
  IN_PREPARATION: {
    key: "IN_PREPARATION",
    label: "In preparation",
    hint: "Not being worked on yet",
    colorScheme: "gray",
  },
  IN_PROGRESS: {
    key: "IN_PROGRESS",
    label: "In progress",
    hint: "Being written now",
    colorScheme: "blue",
  },
  SUBMITTED_OR_APPROVED: {
    key: "SUBMITTED_OR_APPROVED",
    label: "Submitted or approved",
    hint: "Already signed off or awaiting approval",
    colorScheme: "orange",
  },
  UNKNOWN: {
    key: "UNKNOWN",
    label: "Stage unknown",
    hint: "No workflow stage recorded",
    colorScheme: "gray",
  },
};

/**
 * The bucket a requirement is SHOWN in. An answer that cites foundation data has, by definition, been worked
 * on — so "In preparation" (the backend's bucket for START / AI_QUEUE / EXPERT_REVIEW / PENDING) is shown as
 * "In progress". The backend enum is left alone; this only folds the display.
 */
export function displayBucketKey(bucket) {
  if (bucket === IMPACT_BUCKET.IN_PREPARATION.key) return IMPACT_BUCKET.IN_PROGRESS.key;
  return IMPACT_BUCKET[bucket] ? bucket : IMPACT_BUCKET.UNKNOWN.key;
}

/** The bucket for a value off the wire, tolerant of a null or a name this build does not know. */
export function bucketOf(bucket) {
  return IMPACT_BUCKET[bucket] ?? IMPACT_BUCKET.UNKNOWN;
}
